import { ClassProvider, ClassSectionComponent, ClassCombatComponent } from '../utils/ClassComponentRegistry';
import { CharacterData } from '../types/character';
import { ClassFeaturesPanel } from '../components/CharacterSheet/ClassFeatures';

/**
 * Universal class provider that uses the new ClassFeaturesPanel component
 * This replaces all individual class providers with a single, data-driven solution
 * Phase 1: Category-Based Tabbed Interface with Quick Reference Mode
 */
export class UniversalClassProvider implements ClassProvider {
  public className: string;
  public characterSheetSections: ClassSectionComponent[];
  public combatTrackerComponents: ClassCombatComponent[];

  constructor(className: string) {
    this.className = className;

    // Define the new class features panel section
    this.characterSheetSections = [
      {
        component: ClassFeaturesPanel,
        title: 'Class Features',
        priority: 'high',
        defaultExpanded: true,
        order: 100, // Display after basic character info but before other sections
        selfContained: false // Wrapped in DraggableSection by CharacterSheet
      }
    ];

    // No combat components for the standardized system (combat is handled separately)
    this.combatTrackerComponents = [];
  }

  /**
   * Validate that the character has the required basic data
   */
  validateCharacter(character: CharacterData): boolean {
    // Basic validation - character should have a class that matches this provider
    return character.class === this.className;
  }

  /**
   * Initialize character with any class-specific defaults if needed
   * For the standardized system, we rely on the class JSON data and don't need special initialization
   */
  initializeCharacter(character: CharacterData): Partial<CharacterData> {
    // No special initialization needed - ClassFeaturesDisplay reads from class data files
    return {};
  }
}

/**
 * Factory function to create universal providers for all classes
 */
export function createUniversalProviders(): UniversalClassProvider[] {
  const classNames = [
    'bard', 'brawler', 'druid', 'guardian', 'ranger',
    'rogue', 'seraph', 'sorcerer', 'warrior', 'wizard'
  ];

  return classNames.map(className => new UniversalClassProvider(className));
}

/**
 * Helper function to create a single universal provider for a specific class
 */
export function createUniversalProvider(className: string): UniversalClassProvider {
  return new UniversalClassProvider(className);
}